import {
  mkdirSync,
  writeFileSync,
  readFileSync,
  readdirSync,
  rmSync,
  renameSync,
  statSync,
  existsSync,
} from 'node:fs';
import { join } from 'node:path';
import sharp from 'sharp';
import { planContext } from '../billing/context.js';
import { getPlanLimits } from '../billing/entitlements.js';

export interface SessionManifest {
  id: string;
  startedAt: string;
  endedAt: string | null;
  duration: number;
  actionCount: number;
  actions: ActionEntry[];
}

export interface ActionEntry {
  seq: number;
  timestamp: string;
  tool: string;
  args: Record<string, unknown>;
  frame: string;
  pageUrl: string;
  pageTitle: string;
  screenshotFailed?: boolean;
}

export interface SessionSummary {
  id: string;
  startedAt: string;
  endedAt: string | null;
  duration: number;
  actionCount: number;
  firstUrl: string | null;
  hasThumbnail: boolean;
}

/** Create session directory with frames/ subdirectory. */
export function createSessionDir(baseDir: string, sessionId: string): string {
  const sessionDir = join(baseDir, sessionId);
  mkdirSync(join(sessionDir, 'frames'), { recursive: true });
  return sessionDir;
}

/** Save a screenshot frame as PNG. */
export function saveFrame(
  baseDir: string,
  sessionId: string,
  seq: number,
  pngBuffer: Buffer,
): string {
  const filename = `${String(seq).padStart(4, '0')}.png`;
  const framePath = join(baseDir, sessionId, 'frames', filename);
  writeFileSync(framePath, pngBuffer);
  return filename;
}

/** Save a thumbnail (first frame resized to 320px wide). */
export async function saveThumbnail(
  baseDir: string,
  sessionId: string,
  pngBuffer: Buffer,
): Promise<void> {
  if (!pngBuffer || pngBuffer.length === 0) return;
  try {
    const sessionDir = join(baseDir, sessionId);
    const thumbnailPath = join(sessionDir, 'thumbnail.png');
    const resized = await sharp(pngBuffer).resize(320).png().toBuffer();
    // Re-ensure dir exists after the async sharp operation (it may have been
    // created by createSessionDir but we create it here too for safety).
    mkdirSync(sessionDir, { recursive: true });
    writeFileSync(thumbnailPath, resized);
  } catch (err) {
    console.error(
      `[browserforge] Thumbnail generation failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** Atomically write session manifest (write .tmp, then rename). */
export function writeManifest(
  baseDir: string,
  sessionId: string,
  manifest: SessionManifest,
): void {
  const manifestPath = join(baseDir, sessionId, 'session.json');
  const tmpPath = manifestPath + '.tmp';
  writeFileSync(tmpPath, JSON.stringify(manifest, null, 2));
  renameSync(tmpPath, manifestPath);
}

/** Read a session manifest. */
export function readManifest(
  baseDir: string,
  sessionId: string,
): SessionManifest | null {
  try {
    const raw = readFileSync(join(baseDir, sessionId, 'session.json'), 'utf-8');
    return JSON.parse(raw) as SessionManifest;
  } catch {
    return null;
  }
}

export interface ListSessionsOptions {
  limit?: number;
  cursor?: string;
  query?: string;
}

export interface ListSessionsResult {
  sessions: SessionSummary[];
  nextCursor: string | null;
  total: number;
}

/** List sessions sorted by date desc (newest first), with optional pagination and filtering. */
export function listSessions(
  baseDir: string,
  options: ListSessionsOptions = {},
): ListSessionsResult {
  const { limit, cursor, query } = options;

  let entries: string[];
  try {
    entries = readdirSync(baseDir);
  } catch {
    return { sessions: [], nextCursor: null, total: 0 };
  }

  const all: SessionSummary[] = [];
  for (const entry of entries) {
    const manifest = readManifest(baseDir, entry);
    if (!manifest) continue;
    all.push({
      id: manifest.id,
      startedAt: manifest.startedAt,
      endedAt: manifest.endedAt,
      duration: manifest.duration,
      actionCount: manifest.actionCount,
      firstUrl: manifest.actions[0]?.pageUrl ?? null,
      hasThumbnail: existsSync(join(baseDir, entry, 'thumbnail.png')),
    });
  }

  all.sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );

  // Apply query filter
  let filtered = all;
  if (query) {
    const q = query.toLowerCase();
    filtered = all.filter(
      (s) =>
        (s.firstUrl?.toLowerCase().includes(q) ?? false) ||
        s.id.toLowerCase().includes(q),
    );
  }

  const total = filtered.length;

  // Apply cursor — find the position of the cursor ID, start after it
  let startIndex = 0;
  if (cursor) {
    const idx = filtered.findIndex((s) => s.id === cursor);
    if (idx === -1) {
      // Cursor not found — return empty
      return { sessions: [], nextCursor: null, total };
    }
    startIndex = idx + 1;
  }

  const afterCursor = filtered.slice(startIndex);

  // Apply limit
  let sessions: SessionSummary[];
  let nextCursor: string | null = null;
  if (limit !== undefined) {
    sessions = afterCursor.slice(0, limit);
    nextCursor = afterCursor.length > limit ? (sessions[sessions.length - 1]?.id ?? null) : null;
  } else {
    sessions = afterCursor;
  }

  return { sessions, nextCursor, total };
}

/** Get a full session manifest by ID. */
export function getSession(
  baseDir: string,
  sessionId: string,
): SessionManifest | null {
  return readManifest(baseDir, sessionId);
}

/** Delete a session directory. */
export function deleteSession(baseDir: string, sessionId: string): void {
  const sessionDir = join(baseDir, sessionId);
  rmSync(sessionDir, { recursive: true, force: true });
}

/** Delete sessions older than the given number of days. */
export function deleteSessionsOlderThan(
  baseDir: string,
  days: number,
): number {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const { sessions } = listSessions(baseDir);
  let deleted = 0;
  for (const session of sessions) {
    if (new Date(session.startedAt).getTime() < cutoff) {
      deleteSession(baseDir, session.id);
      deleted++;
    }
  }
  return deleted;
}

/** Delete oldest sessions if over the max limit. */
export function deleteOldestIfOverLimit(
  baseDir: string,
  maxSessions: number,
): number {
  const { sessions } = listSessions(baseDir); // sorted newest first
  if (sessions.length <= maxSessions) return 0;

  let deleted = 0;
  // Delete from the end (oldest) until within limit
  const toDelete = sessions.slice(maxSessions);
  for (const session of toDelete) {
    deleteSession(baseDir, session.id);
    deleted++;
  }
  return deleted;
}

/** Run both retention checks, enforcing plan limits. */
export function runRetention(
  baseDir: string,
  maxSessions: number,
  maxAgeDays: number,
): { deletedByAge: number; deletedByLimit: number } {
  const planLimits = getPlanLimits(planContext.getPlan());

  // Plan caps: if plan limit is -1 (unlimited), use config value; otherwise use the stricter of the two.
  const effectiveMaxSessions =
    planLimits.maxSessions === -1
      ? maxSessions
      : Math.min(maxSessions, planLimits.maxSessions);
  const effectiveMaxAgeDays =
    planLimits.maxAgeDays === -1
      ? maxAgeDays
      : Math.min(maxAgeDays, planLimits.maxAgeDays);

  const deletedByAge = deleteSessionsOlderThan(baseDir, effectiveMaxAgeDays);
  const deletedByLimit = deleteOldestIfOverLimit(baseDir, effectiveMaxSessions);
  return { deletedByAge, deletedByLimit };
}
